<template>
  <div class="content-block">
    <div
      v-if="hasEditPermissions"
      class="app-buttons"
    >
      <dx-button
        id="create"
        icon="plus"
        type="normal"
        :text="$t('general.create')"
        :hint="$t('locations.dictionaryItem.create.hint')"
        @click="onCreate"
      />
    </div>

    <dx-tree-list
      class="data-grid"
      id="items-tree-list"
      :data-source="items"
      :show-borders="true"
      key-expr="id"
      parent-id-expr="parent.id"
      :show-row-lines="true"
      ref="dataGrid"
      :height="gridHeight"
      @row-click="onRowClick"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @initialized=initializedGrid
    >
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="true"/>
      <dx-scrolling mode="virtual" />
      <dx-paging :enabled="true" />
      <dx-row-dragging
        :on-reorder="onReorder"
        :allow-drop-inside-item="hasEditPermissions"
        :show-drag-icons="hasEditPermissions"
      />
      <dx-sorting mode="multiple" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        data-field="name"
        :caption="$t('locations.dictionaryItem.name')"
        :allow-header-filtering="false"
        :allow-filtering="true"
      />
    </dx-tree-list>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import baseView from "../base";
import appInfo from "../../app-info";

import {
  DxTreeList, DxColumn, DxFilterRow, DxHeaderFilter, DxPaging, DxScrolling, DxRowDragging,
  DxStateStoring, DxSorting
} from 'devextreme-vue/tree-list';
import { DxButton } from 'devextreme-vue';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { putDictionaryItem, getUserLocations } from '@/services/api';
import { AclHandler } from "@/services/acl-handler";

import { DataBacked } from "@/mixins/dataBackedMixin";
import { DataDrid } from "@/mixins/dataDridMixin";
import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT
} from '@/shared/consts/layout';

import { findIndex } from 'lodash';

export default {
    extends: baseView,
    mixins: [DataBacked, DataDrid],
    components: {
      DxTreeList, DxColumn, DxFilterRow, DxHeaderFilter, DxPaging, DxScrolling, DxRowDragging, DxButton,
      DxStateStoring, CustomColumnChooser, DxSorting
    },
    data() {
      return {
        isCloudMode: this.$store.getters.isCloudMode,
        items: []
      };
    },
    computed: {
      appUser() {
        return this.$store.getters.user;
      },
      aclUser() {
        return new AclHandler(this.appUser, this.isCloudMode);
      },
      hasEditPermissions() {
        return this.aclUser.hasGrantForOperation('PAGE_LOCATION', 'EDIT') || false;
      },
      sumGap() {
        return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
      },
      gridHeight() {
        const appHeightSize = this.$store.getters.appContainerSize.height;
        return (appHeightSize || 800) - this.sumGap;
      },
      gridBodyHeight() {
        return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT;
      },
    },
    methods: {
      onEditorPreparing(e) {
        this.applyFilterRowTimeout(e);
      },
      onRowClick(e) {
        const target = e.event.target;
        const isIconExpandCollapse = target.closest('.dx-treelist-collapsed') || target.closest('.dx-treelist-expanded');
        const isCommandDrag = target.closest('.dx-command-drag');
        if (window.getSelection().focusOffset !== window.getSelection().anchorOffset || isIconExpandCollapse || isCommandDrag || !e.data.id)
          return;

        if (e.event.metaKey || e.event.ctrlKey) {
          const routeData = this.$router.resolve({
            name: "location",
            params: { id: e.data.id }
          });
          window.open(routeData.href, "_blank");
        } else {
          this.$router.push({
            name: "location",
            params: { id: e.data.id }
          });
        }
      },
      async loadData() {
        try {
          this.items = await getUserLocations();
          this.items.forEach(el => {
            if (el.parent) {
              if (findIndex(this.items, ['code', el.parent.code]) === -1 ) {
                el.parent = null;
              }
            }
          });
        } catch(error) {
          this.onDataError(error);
        }
      },
      async onReorder(e) {
        if (!e.dropInsideItem)
          return;

        const visibleRows = e.component.getVisibleRows();
        const item = e.itemData;
        const parent = visibleRows[e.toIndex].data;

        try {
          item.parent = parent;
          await putDictionaryItem(item.id, item);
          e.component.refresh();
        } catch(err) {
          this.onDataError(err);
        }
      },
      onCreate() {
        this.$router.push({name: 'location', params: { id: -1 }});
      },
    },
    created() {
      document.title = `${appInfo.title} - ${this.$t('menu.locations')}`;
      this.$store.commit("setBreadcrumb", [{
        link: '/locations',
        title: this.$t('menu.locations')
      }]);

      this.gridStateKey = 'gridLocations';

      this.loadData();
    },
    beforeDestroy() {
      const state = this.dataGrid.state();
      this.stateSave(state);
    },
  };
</script>
  
<style lang="scss">
</style>